import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/auth.service';
import userService from '../services/user.service';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = authService.getToken();
      const savedUser = authService.getUser();

      if (token && savedUser) {
        setUser(savedUser);
        setIsAuthenticated(true);

        // Optionally refresh user data from server
        try {
          const freshUserData = await userService.getProfile();
          setUser(freshUserData);
          authService.setUser(freshUserData);
        } catch (error) {
          console.error('Error refreshing user data:', error);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const userData = response.user || response;
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true, data: response };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await authService.register(email, password, fullName);
      const userData = response.user || response;
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true, data: response };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      authService.clearAuth();
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    authService.setUser(userData);
  };

  // Role checking helpers
  const isAdmin = () => user?.role === 'ADMIN';
  const isProvider = () => user?.role === 'PROVIDER';
  const isRegularUser = () => user?.role === 'USER';

  const hasRole = (role) => user?.role === role;

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    isAdmin,
    isProvider,
    isRegularUser,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
